import React, {useState} from "react";

import {useQuotes} from "../QuotesContext";
import QuoteInfoBar from "../QuoteInfoBar";
import BotIcon from "../BotIcon";

import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LoadingButton from "@mui/lab/LoadingButton";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel/FormControlLabel";
import useDialog from "../../../hooks/useDialog";


const QuoteVariants = ({quote}) => {
    const {accommodations, isUpdatingQuote} = useQuotes();

    return <Stack direction="column" spacing={2}>
        {
            quote.createdByAutoReply === true && <Alert severity="info" icon={<BotIcon />}>
                <Typography variant="body2" fontWeight="light">
                    Il preventivo è stato creato in automatico.
                </Typography>
            </Alert>
        }
        {
            quote.datesChanged === true && <Alert severity="warning" icon={<EditCalendarIcon />}>
                <Typography variant="body2" fontWeight="light">
                    Le date di arrivo e partenza non coicidono con quelle richieste dall'ospite.
                </Typography>
            </Alert>
        }
        {
            quote.quoteVariants?.map((quoteVariant, i) => (
                <React.Fragment key={quoteVariant.id}>
                    <Typography variant="h6" color="primary">
                        Alternativa {i + 1}
                    </Typography>
                    <Card sx={{py: 2, px: 2, mb: 3, mt: 1}}>
                        <QuoteInfoBar arrival={quoteVariant.getArrival()}
                                      departure={quoteVariant.getDeparture()}
                                      adults={quote.adults}
                                      children={quote.children}
                                      petsNumber={quote.petsNumber}/>
                        <Divider sx={{mt: 2}}/>
                        <List>
                            {
                                quoteVariant.quoteItems.map(quoteItem => (
                                    accommodations.isLoading ?
                                        <Stack flexGrow={1} key={quoteItem.id}> <Skeleton height={60}/> </Stack>
                                        :
                                        <ListItem key={quoteItem.id}
                                                  divider={quoteVariant.quoteItems.length > 1}
                                                  disableGutters>
                                            <Stack flexGrow={1}>
                                                <Typography variant="body1" fontWeight="normal">
                                                    {accommodations.findApartment(quoteItem.apartmentId)?.name}
                                                </Typography>
                                                <Typography variant="caption" fontWeight="light">
                                                    {accommodations.findApartment(quoteItem.apartmentId)?.accommodation?.name}
                                                </Typography>
                                            </Stack>
                                            <Typography>
                                                {quoteItem.getPrice()} €
                                            </Typography>
                                        </ListItem>
                                ))
                            }
                        </List>
                        {
                            quoteVariant.quoteItems.length > 1 &&
                            <Typography variant="h6" textAlign="right" color="success.main" fontWeight='regular'
                                        sx={{mb: 2}}>
                                Tot: {quoteVariant.getPrice()} €
                            </Typography>
                        }
                        <Box textAlign="right">
                            {
                                quoteVariant.confirmed === true ?
                                    <Stack direction="row-reverse" spacing={1}>
                                        <CheckCircleIcon color="success"/>
                                        <Typography color="success.main" display="inline">Alternativa
                                            confermata</Typography>
                                    </Stack>
                                    :
                                    <ConfirmationButton loading={isUpdatingQuote}
                                                        disabled={quote.confirmed}
                                                        quoteVariant={quoteVariant}>
                                        Conferma e crea prenotazione
                                    </ConfirmationButton>
                            }
                        </Box>
                    </Card>
                </React.Fragment>
            ))
        }
    </Stack>;
}

const ConfirmationButton = ({quoteVariant, ...props}) => {
    const {confirmSelectedQuote} = useQuotes();
    const confirmDialog = useDialog();

    const handleConfirm = async (options) => {
        confirmDialog.setIsLoading(true);
        await confirmSelectedQuote(quoteVariant, {sendEmailAfterCreate: options.sendEmailAfterCreate});
        confirmDialog.setIsLoading(false);
    }

    return <>
        <LoadingButton variant="outlined"
                       size="small"
                       color="primary"
                       onClick={confirmDialog.open}
                       {...props}>
            Conferma e crea prenotazione
        </LoadingButton>
        {
            confirmDialog.show && <ConfirmationDialog onCancel={confirmDialog.close}
                                                      onConfirm={handleConfirm}
                                                      isLoading={confirmDialog.loading}/>
        }
    </>
}


const ConfirmationDialog = ({onConfirm, onCancel, isLoading}) => {
    const [sendEmailAfterCreate, setSendEmailAfterCreate] = useState(true);

    const toggleSendConfirmationEmail = () => {
        setSendEmailAfterCreate(prev => !prev);
    }

    return <Dialog open={true} onClose={onCancel}>
        <DialogTitle>
            Conferma preventivo
        </DialogTitle>
        <DialogContent>
            <Stack spacing={3}>
                <Typography variant="body1">
                    Attenzione: conferma il preventivo solo dopo che l'ospite ha accettato la tua proposta. Verrà creata
                    una nuova prenotazione nel tableau e in prossimità della data di arrivo verrà inviata una email per
                    il check-in online. Vuoi confermare il preventivo e creare una prenotazione?
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                    Opzioni:
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            color="primary"
                            checked={sendEmailAfterCreate}
                            onChange={toggleSendConfirmationEmail}/>
                    }
                    label={sendEmailAfterCreate ? "Invia l'email di conferma prenotazione all'ospite" : "Non inviare l'email di conferma all'ospite"}
                />
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={onCancel} color="primary" variant="outlined" disabled={isLoading}>
                Annulla
            </Button>
            <LoadingButton onClick={() => onConfirm({sendEmailAfterCreate})} color="primary" variant="contained" loading={isLoading}>
                Conferma
            </LoadingButton>
        </DialogActions>
    </Dialog>
}

export default QuoteVariants;